import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    MenuItem,
    Tooltip,
    Typography,
    Paper
} from '@mui/material';
import { Add, Delete, Edit, EventSeat, NotificationsActive, Block, Print } from '@mui/icons-material';
import Navbar from '../setup/Navbar';
import {
    fetchDineInTables,
    createDineInTable,
    updateDineInTable,
    deleteDineInTable,
    reset
} from '../../features/setup/dineInTableSlice';
import {
    fetchDineInWaitlist,
    createDineInWaitlistToken,
    callDineInWaitlistToken,
    seatDineInWaitlistToken,
    cancelDineInWaitlistToken,
    reset as resetWaitlist
} from '../../features/setup/dineInWaitlistSlice';
import {
    broadcastWaitlistAnnouncement,
    buildWaitlistAnnouncementPayload,
    normalizeQueueDate
} from '../../utils/waitlistAnnouncements';

const formatDateTime = (value) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleString('en-PK');
};

const formatTokenNumber = (tokenNo) => String(tokenNo || 0).padStart(3, '0');

const formatQueueDateLabel = (value) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleDateString('en-PK');
};

const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const canModifyTable = (table) => table.status === 'free' && Number(table.activeOrderCount || 0) === 0;

const getBlockedReason = (table) => {
    if (table.status !== 'free') {
        return 'Reserved or occupied tables cannot be changed from this screen';
    }

    if (Number(table.activeOrderCount || 0) > 0) {
        return 'This table has an active dine-in draft or parked order';
    }

    return '';
};

const getStatusColor = (status) => {
    if (status === 'free') return 'success';
    if (status === 'reserved') return 'warning';
    if (status === 'occupied') return 'error';
    return 'default';
};

const getWaitlistStatusColor = (status) => {
    if (status === 'waiting') return 'info';
    if (status === 'called') return 'warning';
    if (status === 'seated') return 'success';
    if (status === 'cancelled') return 'default';
    return 'default';
};

export default function DineInTableManagement() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { tables, isLoading, isError, isSuccess, message } = useSelector((state) => state.dineInTables);
    const {
        waitlist,
        isLoading: isWaitlistLoading,
        isError: isWaitlistError,
        isSuccess: isWaitlistSuccess,
        message: waitlistMessage
    } = useSelector((state) => state.dineInWaitlist);
    const { user } = useSelector((state) => state.auth);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [tableLabel, setTableLabel] = useState('');
    const [seatingCapacity, setSeatingCapacity] = useState('4');
    const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
    const [seatDialogOpen, setSeatDialogOpen] = useState(false);
    const [selectedWaitToken, setSelectedWaitToken] = useState(null);
    const [assignedTableLabel, setAssignedTableLabel] = useState('');
    const [bookingForm, setBookingForm] = useState({
        customerName: '',
        customerPhone: '',
        personsCount: '2',
        preferredTableLabel: '',
        notes: ''
    });

    useEffect(() => {
        dispatch(fetchDineInTables());
        dispatch(fetchDineInWaitlist());
    }, [dispatch]);

    useEffect(() => {
        if (isSuccess && message) {
            alert(message);
            dispatch(reset());
            setDialogOpen(false);
            setEditId(null);
            setTableLabel('');
            setSeatingCapacity('4');
            dispatch(fetchDineInTables());
        }

        if (isError && message) {
            alert(message);
            dispatch(reset());
        }
    }, [isSuccess, isError, message, dispatch]);

    useEffect(() => {
        if (isWaitlistSuccess && waitlistMessage) {
            alert(waitlistMessage);
            dispatch(resetWaitlist());
            setBookingDialogOpen(false);
            setSeatDialogOpen(false);
            setSelectedWaitToken(null);
            setAssignedTableLabel('');
            setBookingForm({ customerName: '', customerPhone: '', personsCount: '2', preferredTableLabel: '', notes: '' });
            dispatch(fetchDineInWaitlist());
        }

        if (isWaitlistError && waitlistMessage) {
            alert(waitlistMessage);
            dispatch(resetWaitlist());
        }
    }, [isWaitlistSuccess, isWaitlistError, waitlistMessage, dispatch]);

    const tableSummary = useMemo(() => ({
        total: tables.length,
        free: tables.filter((table) => table.status === 'free').length,
        busy: tables.filter((table) => table.status !== 'free').length,
        totalSeats: tables.reduce((sum, table) => sum + Number(table.seatingCapacity || 0), 0)
    }), [tables]);

    const waitlistSummary = useMemo(() => ({
        active: waitlist.filter((item) => ['waiting', 'called'].includes(item.status)).length,
        waiting: waitlist.filter((item) => item.status === 'waiting').length,
        called: waitlist.filter((item) => item.status === 'called').length,
        seatedToday: waitlist.filter((item) => item.status === 'seated' && normalizeQueueDate(item.queueDate) === normalizeQueueDate(new Date())).length
    }), [waitlist]);

    const freeTables = useMemo(
        () => tables.filter((table) => table.status === 'free'),
        [tables]
    );

    const getSuitableTables = (personsCount) => (
        freeTables.filter((table) => Number(table.seatingCapacity || 0) >= Number(personsCount || 0))
    );

    const getQueuePosition = (item) => {
        if (!['waiting', 'called'].includes(item.status)) {
            return '-';
        }

        return waitlist.filter((entry) => (
            entry.queueDate === item.queueDate
            && ['waiting', 'called'].includes(entry.status)
            && Number(entry.tokenNo || 0) <= Number(item.tokenNo || 0)
        )).length;
    };

    const openDialog = (table = null) => {
        if (table) {
            setEditId(table.reservationID);
            setTableLabel(table.tableLabel || '');
            setSeatingCapacity(String(table.seatingCapacity || 4));
        } else {
            setEditId(null);
            setTableLabel('');
            setSeatingCapacity('4');
        }

        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditId(null);
        setTableLabel('');
        setSeatingCapacity('4');
    };

    const closeBookingDialog = () => {
        setBookingDialogOpen(false);
        setBookingForm({ customerName: '', customerPhone: '', personsCount: '2', preferredTableLabel: '', notes: '' });
    };

    const closeSeatDialog = () => {
        setSeatDialogOpen(false);
        setSelectedWaitToken(null);
        setAssignedTableLabel('');
    };

    const handleSave = () => {
        if (!tableLabel.trim()) {
            alert('Table label is required');
            return;
        }

        const parsedCapacity = Number.parseInt(String(seatingCapacity), 10);

        if (!Number.isInteger(parsedCapacity) || parsedCapacity < 1 || parsedCapacity > 50) {
            alert('Person capacity must be between 1 and 50');
            return;
        }

        const payload = {
            tableLabel,
            seatingCapacity: parsedCapacity
        };

        if (editId) {
            dispatch(updateDineInTable({ reservationID: editId, data: payload }));
            return;
        }

        dispatch(createDineInTable(payload));
    };

    const handleDelete = (table) => {
        if (!window.confirm(`Delete table ${table.tableLabel}?`)) {
            return;
        }

        dispatch(deleteDineInTable(table.reservationID));
    };

    const handleBookingInputChange = (field) => (event) => {
        setBookingForm((prev) => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const printTokenSlip = (item) => {
        if (!item) {
            return;
        }

        const token = formatTokenNumber(item.tokenNo);
        const customerName = escapeHtml(String(item.customerName || '-'));
        const customerPhone = escapeHtml(String(item.customerPhone || '-'));
        const personsCount = Number(item.personsCount || 0);
        const preferredTable = escapeHtml(item.preferredTableLabel || '-');
        const queueDate = escapeHtml(formatQueueDateLabel(item.queueDate || new Date()));
        const createdAt = escapeHtml(formatDateTime(item.createdAt || new Date()));
        const notes = escapeHtml(String(item.notes || '').trim());

        const printWindow = window.open('', '_blank', 'width=420,height=650');

        if (!printWindow) {
            alert('Pop-up blocked. Please allow pop-ups to print token.');
            return;
        }

        const html = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Token ${token}</title>
                <meta charset="utf-8" />
                <style>
                    * { box-sizing: border-box; }
                    body {
                        margin: 0;
                        padding: 20px;
                        font-family: Arial, sans-serif;
                        background: #f4f4f4;
                    }
                    .slip {
                        width: 320px;
                        margin: 0 auto;
                        border: 2px dashed #111;
                        background: #fff;
                        padding: 14px;
                    }
                    .center { text-align: center; }
                    .title {
                        font-size: 16px;
                        font-weight: 700;
                        letter-spacing: 0.6px;
                        margin-bottom: 6px;
                        text-transform: uppercase;
                    }
                    .token {
                        font-size: 46px;
                        font-weight: 700;
                        line-height: 1;
                        margin: 10px 0;
                    }
                    .line {
                        border-top: 1px dashed #555;
                        margin: 10px 0;
                    }
                    .row {
                        display: flex;
                        justify-content: space-between;
                        font-size: 13px;
                        margin: 6px 0;
                        gap: 10px;
                    }
                    .label { color: #444; }
                    .value {
                        color: #111;
                        font-weight: 600;
                        text-align: right;
                        flex: 1;
                        word-break: break-word;
                    }
                    .note {
                        font-size: 12px;
                        margin-top: 8px;
                        padding: 8px;
                        border: 1px solid #ddd;
                        background: #fafafa;
                    }
                    .footer {
                        text-align: center;
                        font-size: 11px;
                        margin-top: 14px;
                        color: #444;
                    }
                    @media print {
                        body { background: #fff; padding: 0; }
                        .slip { border: 1px dashed #111; width: 100%; max-width: 100%; }
                    }
                </style>
            </head>
            <body>
                <div class="slip">
                    <div class="center title">Dine-In Waiting Token</div>
                    <div class="line"></div>
                    <div class="center">Your Token Number</div>
                    <div class="center token">${token}</div>
                    <div class="center" style="font-size: 12px;">Please keep this slip and wait for your number call.</div>
                    <div class="line"></div>
                    <div class="row"><span class="label">Customer</span><span class="value">${customerName}</span></div>
                    <div class="row"><span class="label">Phone</span><span class="value">${customerPhone}</span></div>
                    <div class="row"><span class="label">Persons</span><span class="value">${personsCount}</span></div>
                    <div class="row"><span class="label">Preferred Table</span><span class="value">${preferredTable}</span></div>
                    <div class="row"><span class="label">Queue Date</span><span class="value">${queueDate}</span></div>
                    <div class="row"><span class="label">Issued At</span><span class="value">${createdAt}</span></div>
                    ${notes ? `<div class="note"><strong>Note:</strong> ${notes}</div>` : ''}
                    <div class="footer">Token ${token} | Thank you</div>
                </div>
                <script>
                    window.onload = function () {
                        window.print();
                    };
                </script>
            </body>
            </html>
        `;

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
    };

    const handleCreateBooking = async (shouldPrint = false) => {
        const customerName = String(bookingForm.customerName || '').trim();
        const personsCount = Number.parseInt(String(bookingForm.personsCount || ''), 10);

        if (!customerName) {
            alert('Customer name is required');
            return;
        }

        if (!Number.isInteger(personsCount) || personsCount < 1 || personsCount > 50) {
            alert('Persons count must be between 1 and 50');
            return;
        }

        const payload = {
            customerName,
            customerPhone: bookingForm.customerPhone,
            personsCount,
            preferredTableLabel: bookingForm.preferredTableLabel || null,
            notes: bookingForm.notes,
            userID: user?.userID || null
        };

        try {
            const response = await dispatch(createDineInWaitlistToken(payload)).unwrap();

            if (shouldPrint) {
                printTokenSlip({
                    waitlistID: response.waitlistID,
                    tokenNo: response.tokenNo,
                    queueDate: response.queueDate,
                    customerName,
                    customerPhone: payload.customerPhone,
                    personsCount,
                    preferredTableLabel: payload.preferredTableLabel,
                    notes: payload.notes,
                    createdAt: new Date()
                });
            }
        } catch (error) {
            // Error handling is already managed through the slice state.
        }
    };

    const handleCallToken = async (item) => {
        try {
            await dispatch(callDineInWaitlistToken(item.waitlistID)).unwrap();
            broadcastWaitlistAnnouncement(buildWaitlistAnnouncementPayload(item));
        } catch (error) {
            // Error handling is already managed through the slice state.
        }
    };

    const openSeatDialog = (item) => {
        const matchingTables = getSuitableTables(item.personsCount);
        const preferredMatchingTable = matchingTables.find((table) => table.tableLabel === item.preferredTableLabel);
        setSelectedWaitToken(item);
        setAssignedTableLabel(item.assignedTableLabel || preferredMatchingTable?.tableLabel || matchingTables[0]?.tableLabel || '');
        setSeatDialogOpen(true);
    };

    const handleSeatToken = () => {
        if (!selectedWaitToken) {
            return;
        }

        dispatch(seatDineInWaitlistToken({
            waitlistID: selectedWaitToken.waitlistID,
            data: {
                assignedTableLabel: assignedTableLabel || null
            }
        }));
    };

    const handleCancelToken = (item) => {
        if (!window.confirm(`Cancel token ${formatTokenNumber(item.tokenNo)} for ${item.customerName}?`)) {
            return;
        }

        dispatch(cancelDineInWaitlistToken(item.waitlistID));
    };

    return (
        <>
            <Navbar />
            <Box sx={{ p: 2 }}>
                {(isLoading || isWaitlistLoading) && <LinearProgress />}

                <Card>
                    <CardContent>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                            <Box>
                                <Typography variant="h5" fontWeight="bold">Dine In Tables</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    POS me jo dine-in tables nazar aati hain, unko yahan se add, rename, ya remove kiya ja sakta hai.
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip label={`Total: ${tableSummary.total}`} variant="outlined" />
                                <Chip label={`Free: ${tableSummary.free}`} color="success" variant="outlined" />
                                <Chip label={`Busy: ${tableSummary.busy}`} color="warning" variant="outlined" />
                                <Chip label={`Seats: ${tableSummary.totalSeats}`} color="primary" variant="outlined" />
                                <Button startIcon={<Add />} variant="contained" onClick={() => openDialog()}>Add Table</Button>
                            </Stack>
                        </Stack>

                        <Alert severity="info" sx={{ mb: 2 }}>
                            Har table ke sath us ki maximum person capacity save hogi. Busy tables ya active dine-in draft/parked orders wali tables ko yahan se rename ya delete nahi kiya ja sakta.
                        </Alert>

                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Table</strong></TableCell>
                                        <TableCell><strong>Persons</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell><strong>Active Orders</strong></TableCell>
                                        <TableCell><strong>Assigned To</strong></TableCell>
                                        <TableCell><strong>Assigned At</strong></TableCell>
                                        <TableCell><strong>Released At</strong></TableCell>
                                        <TableCell align="center"><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tables.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center">No dine-in tables found.</TableCell>
                                        </TableRow>
                                    )}
                                    {tables.map((table) => {
                                        const isMutable = canModifyTable(table);
                                        const blockedReason = getBlockedReason(table);

                                        return (
                                            <TableRow key={table.reservationID} hover>
                                                <TableCell>{table.tableLabel}</TableCell>
                                                <TableCell>{Number(table.seatingCapacity || 4)}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={table.status} color={getStatusColor(table.status)} />
                                                </TableCell>
                                                <TableCell>{Number(table.activeOrderCount || 0)}</TableCell>
                                                <TableCell>{table.assignedUserName || '-'}</TableCell>
                                                <TableCell>{formatDateTime(table.assignedAt)}</TableCell>
                                                <TableCell>{formatDateTime(table.releasedAt)}</TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title={isMutable ? 'Edit table label' : blockedReason}>
                                                        <span>
                                                            <IconButton
                                                                color="primary"
                                                                size="small"
                                                                disabled={!isMutable}
                                                                onClick={() => openDialog(table)}
                                                            >
                                                                <Edit />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={isMutable ? 'Delete table' : blockedReason}>
                                                        <span>
                                                            <IconButton
                                                                color="error"
                                                                size="small"
                                                                disabled={!isMutable}
                                                                onClick={() => handleDelete(table)}
                                                            >
                                                                <Delete />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>

                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                            Token Management
                        </Typography>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Token system ab is screen se alag kar diya gaya hai. Daily opening date ke sath token numbering 1 se start hoti hai.
                        </Alert>
                        <Button variant="contained" onClick={() => navigate('/DineInTokens')}>
                            Open Dine-In Token Management
                        </Button>
                    </CardContent>
                </Card>

                <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="xs">
                    <DialogTitle>{editId ? 'Rename Dine In Table' : 'Add Dine In Table'}</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            fullWidth
                            margin="dense"
                            label="Table Label"
                            value={tableLabel}
                            onChange={(event) => setTableLabel(event.target.value)}
                            inputProps={{ maxLength: 10 }}
                            helperText="Max 10 characters. Example: T17, VIP1"
                        />
                        <TextField
                            fullWidth
                            margin="dense"
                            type="number"
                            label="Person Capacity"
                            value={seatingCapacity}
                            onChange={(event) => setSeatingCapacity(event.target.value)}
                            inputProps={{ min: 1, max: 50 }}
                            helperText="Example: 4 means is table par 4 persons beth sakte hain"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDialog}>Cancel</Button>
                        <Button onClick={handleSave} variant="contained">Save</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={false} onClose={closeBookingDialog} fullWidth maxWidth="sm">
                    <DialogTitle>Advance Booking Token</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            fullWidth
                            margin="dense"
                            label="Customer Name"
                            value={bookingForm.customerName}
                            onChange={handleBookingInputChange('customerName')}
                        />
                        <TextField
                            fullWidth
                            margin="dense"
                            label="Phone"
                            value={bookingForm.customerPhone}
                            onChange={handleBookingInputChange('customerPhone')}
                        />
                        <TextField
                            fullWidth
                            margin="dense"
                            type="number"
                            label="Persons Count"
                            value={bookingForm.personsCount}
                            onChange={handleBookingInputChange('personsCount')}
                            inputProps={{ min: 1, max: 50 }}
                            helperText="Queue token persons wise generate hoga"
                        />
                        <TextField
                            select
                            fullWidth
                            margin="dense"
                            label="Table No (Optional)"
                            value={bookingForm.preferredTableLabel}
                            onChange={handleBookingInputChange('preferredTableLabel')}
                            helperText="Token allot karte waqt preferred table select kar sakte hain"
                        >
                            <MenuItem value="">No preference</MenuItem>
                            {tables.map((table) => (
                                <MenuItem key={table.reservationID} value={table.tableLabel}>
                                    {table.tableLabel} ({table.seatingCapacity} persons)
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            fullWidth
                            margin="dense"
                            label="Notes"
                            value={bookingForm.notes}
                            onChange={handleBookingInputChange('notes')}
                            multiline
                            minRows={2}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeBookingDialog}>Cancel</Button>
                        <Button onClick={() => handleCreateBooking(true)} variant="outlined">Create & Print</Button>
                        <Button onClick={() => handleCreateBooking(false)} variant="contained">Create Token</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={false} onClose={closeSeatDialog} fullWidth maxWidth="sm">
                    <DialogTitle>Mark Token Seated</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            {selectedWaitToken
                                ? `Token #${formatTokenNumber(selectedWaitToken.tokenNo)} | ${selectedWaitToken.customerName} | ${selectedWaitToken.personsCount} persons`
                                : ''}
                        </Typography>
                        <TextField
                            select
                            fullWidth
                            margin="dense"
                            label="Assign Table (Optional)"
                            value={assignedTableLabel}
                            onChange={(event) => setAssignedTableLabel(event.target.value)}
                            helperText="Sirf free aur matching capacity wali tables list me dikh rahi hain"
                        >
                            <MenuItem value="">No table selected</MenuItem>
                            {selectedWaitToken && getSuitableTables(selectedWaitToken.personsCount).map((table) => (
                                <MenuItem key={table.reservationID} value={table.tableLabel}>
                                    {table.tableLabel} ({table.seatingCapacity} persons)
                                </MenuItem>
                            ))}
                        </TextField>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeSeatDialog}>Cancel</Button>
                        <Button onClick={handleSeatToken} variant="contained" color="success">Mark Seated</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
}